import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Check, Rocket } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/cgjm5igj_FB%20Cover%20logo.png";

const STEPS = [
  "Free full access to the member area during the beta",
  "Read the Member Handbook before Monday's live Zoom call",
  "Print your tracker and log your baseline measurements",
  "Share your feedback and help shape the program",
];

export default function BetaSignup() {
  usePageTitle("Beta Tester Sign Up");
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register-beta", form);
      applyAuth(data);
      toast.success("Welcome to the beta! Your onboarding email is on its way.");
      navigate("/portal");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const cls = "w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F5F5F4]">
      <div className="w-full max-w-3xl grid md:grid-cols-2 bg-white rounded-2xl border border-[#E7E5E4] shadow-sm overflow-hidden">
        <div className="bg-navy text-white p-8 relative overflow-hidden">
          <div className="brand-blob bg-teal" style={{ width: 240, height: 240, bottom: -80, right: -60 }} />
          <div className="relative">
            <span className="inline-flex items-center gap-2 bg-teal/20 text-teal-100 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full">
              <Rocket className="w-3.5 h-3.5" /> Thank you for helping
            </span>
            <h1 className="mt-4 font-heading text-3xl leading-tight">Join the My Best Body Club beta</h1>
            <p className="mt-3 text-white/75 text-sm leading-relaxed">
              Be one of the first to test the program — your journey starts Now.
            </p>
            <ul className="mt-6 space-y-3">
              {STEPS.map((s) => (
                <li key={s} className="flex items-start gap-2.5 text-sm text-white/85">
                  <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="p-8">
          <Link to="/" className="flex justify-center mb-5"><img src={LOGO} alt="MBBC" className="h-14 w-14 object-contain" /></Link>
          <h2 className="font-heading text-2xl text-navy text-center">Create your beta account</h2>
          <p className="mt-1 text-center text-sm text-[#57534E]">Free access — no payment needed.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input data-testid="beta-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={cls} />
            <input data-testid="beta-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")} className={cls} />
            <input data-testid="beta-password" type="password" required minLength={6} placeholder="Choose a password (min 6 chars)" value={form.password} onChange={set("password")} className={cls} />
            {err && <p data-testid="beta-error" className="text-sm text-red-600">{err}</p>}
            <button data-testid="beta-submit" disabled={busy} type="submit"
              className="w-full bg-teal text-white hover:bg-teal-dark transition-colors rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Join the beta
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-[#78716C]">
            Already have an account? <Link to="/login" className="text-teal font-medium">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
